/**
 * External Imports
 */
import { FC, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuButton, NeuRadio } from '@neutron/react';

/**
 * Internal Imports
 */
import UserInfo from './UserInfo';
import MultiSelectDropdown from '../../multiSelect/MultiSelectDropdown';
import Loading from '../../shared/Loading';

import { RootState } from '../../../redux/store';
import {
  useGetUserRolesQuery,
  usePostRevokeUserAccessMutation
} from '../../../redux/apis/Admin.api';
import { useGetDivisionsQuery } from '../../../redux/apis/Config.api';
import { usePostUserAccessMutation } from '../../../redux/apis/Users.api';
import { handleModal } from '../../../services/ModalPortal/utils';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, IFacility } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  ProvisioningCard,
  CardHeader,
  ModalCardContent,
  InputHeading,
  ModalFooter,
  ModalTitle
} from './UserModal.styles';

interface ProvisioningModalProps {
  facilities: IFacility[];
  selectedUser: AuthUserProps;
}
const AddProvisioningModal: FC<ProvisioningModalProps> = ({
  facilities = [],
  selectedUser
}) => {
  /** State variables */
  const [showAccessLevel, setShowAccessLevel] = useState<boolean>(true);
  const [accessLevelList, setAccessLevelList] = useState<IFacility[]>([]);
  const [defaultSelection, setDefaultSelection] = useState<IFacility[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState<string>('');
  const [userRoleList, setUserRoleList] = useState<any[]>([]);
  const [selectedUserRole, setSelectedUserRole] = useState<string[]>([]);
  const [defaultUserRoles, setDefaultUserRoles] = useState<any[]>([]);
  const [showButton, setShowButton] = useState<boolean>(false);
  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  const {
    data: divisionsData,
    // isError: isGetDivisionsError,
    // isFetching: isGetDivisionsFetching,
    isLoading: isGetDivisionsLoading
  } = useGetDivisionsQuery(undefined, {
    refetchOnMountOrArgChange: 120
  });
  const {
    data: userRolesData,
    // isError: isUserRolesError,
    // isFetching: isUserRolesFetching,
    isLoading: isUserRolesLoading
  } = useGetUserRolesQuery(undefined, { refetchOnMountOrArgChange: 60 });

  const [
    postUserAccess
    // {
    //   isError: isUserAccessError,
    //   isLoading: isUserAccessLoading,
    //   isSuccess: isUserAccessSuccess
    // }
  ] = usePostUserAccessMutation();
  const [
    postRevokeUserAccess
    // {
    //   isError: isRevokeUserAccessError,
    //   isLoading: isRevokeUserAccessLoading,
    //   isSuccess: isRevokeUserAccessSuccess
    // }
  ] = usePostRevokeUserAccessMutation();

  /** CTA block */
  const handleSelected = (selected: IFacility[]) => {
    const selectedIdArray =
      selected &&
      selected.length > 0 &&
      selected.map((item: IFacility) =>
        selectedAccessLevel === 'division' ? item.id : item.facilityId
      );
    if (selectedIdArray) setSelectedItems(selectedIdArray);
  };

  const handleSelectedUserRoles = (
    selected: { id: string; value: string }[]
  ) => {
    const selectedRoles =
      selected &&
      selected.length > 0 &&
      selected.map((item: { id: string; value: string }) => item.id);
    if (selectedRoles) setSelectedUserRole(selectedRoles);
    // appInsights.trackEvent({
    //   name: 'Config_Edited'
    // });
  };

  const handleRevoke = async () => {
    await postRevokeUserAccess(selectedUser.hcaid);
    setSelectedAccessLevel('');
    setDefaultSelection([]);
    setSelectedUserRole([]);
    setDefaultUserRoles([]);
    setTimeout(() => {
      handleModal(modalContentRef);
    }, 500);
    // appInsights.trackEvent({
    //   name: 'Config_Deleted'
    // });
  };

  const handleSubmit = async () => {
    if (showButton) {
      // appInsights.trackEvent({
      //   name: 'Config_Created'
      // });
      const postParams = {
        employeeId: selectedUser.hcaid,
        access: {
          accessLevel: selectedAccessLevel,
          accessLevelIds:
            selectedAccessLevel === 'master' ? ['HCA'] : selectedItems
        },
        roles: selectedUserRole
      };
      await postUserAccess(postParams);
      setShowButton(false);
      setTimeout(() => {
        handleModal(modalContentRef);
      }, 500);
    }
  };

  const showSubmitButton = () => {
    if (selectedUserRole.length > 0 && selectedAccessLevel) {
      if (selectedAccessLevel === 'master') {
        return true;
      }
      if (selectedAccessLevel !== 'master' && selectedItems.length > 0) {
        return true;
      }
      return false;
    }
    return false;
  };

  const onAccessLevelChange = (selectedLevel: string) => {
    setShowAccessLevel(true);
    if (selectedLevel === 'division') {
      if (selectedUser) {
        // Filtered list
        const filteredDivisions =
          divisionsData?.filter((value: IFacility) => {
            const optionID = value.id;
            return !selectedUser?.access?.accessLevelIds.some(
              (item: String) => item === optionID
            );
          }) || [];
        setAccessLevelList(filteredDivisions);
        // Selected list
        const selectedDivisions =
          divisionsData?.filter((value: IFacility) => {
            const optionID = value.id;
            return selectedUser?.access?.accessLevelIds.some(
              (item: String) => item === optionID
            );
          }) || [];
        setDefaultSelection(selectedDivisions);
      } else {
        setAccessLevelList(divisionsData || []);
      }
    } else if (selectedLevel === 'facility') {
      if (selectedUser) {
        // Filtered list
        const filteredFacilities = facilities.filter((value: IFacility) => {
          const optionID = value.facilityId;
          return !selectedUser?.access?.accessLevelIds.some(
            (item: String) => item === optionID
          );
        });
        setAccessLevelList(filteredFacilities);
        // Selected list
        const selectedFacilities = facilities.filter((value: IFacility) => {
          const optionID = value.facilityId;
          return selectedUser?.access?.accessLevelIds.some(
            (item: String) => item === optionID
          );
        });
        setDefaultSelection(selectedFacilities);
      } else {
        setAccessLevelList(facilities);
      }
    } else {
      setShowAccessLevel(false);
    }
    // Reset selected items when facility/division change
    if (selectedItems && selectedItems.length > 0) {
      setDefaultSelection([]);
    }
    setSelectedAccessLevel(selectedLevel);
  };

  useEffect(() => {
    if (showSubmitButton()) {
      setShowButton(true);
    } else {
      setShowButton(false);
    }
  }, [selectedUserRole, selectedAccessLevel, selectedItems]);

  useEffect(() => {
    if (selectedUser) {
      // Access level radio
      setSelectedAccessLevel(selectedUser?.access?.accessLevel);
      onAccessLevelChange(selectedUser?.access?.accessLevel);
      // Set user role
      if (selectedUser.roles && selectedUser.roles.length > 0) {
        const defaultRoles =
          userRolesData?.filter((item: { id: string; value: string }) =>
            selectedUser.roles.includes(item?.id)
          ) ?? [];
        const availableRoles =
          userRolesData?.filter(
            (item: { id: string; value: string }) =>
              !selectedUser.roles.includes(item?.id)
          ) ?? [];
        setUserRoleList(availableRoles);
        setDefaultUserRoles(defaultRoles);
      } else {
        setUserRoleList(userRolesData || []);
      }
    }
  }, [selectedUser, facilities, userRolesData]);

  /** CTA block - End */
  return (
    <ProvisioningCard
      id="Modal-Provision"
      className="show-modal"
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <ModalTitle>Edit User</ModalTitle>
        <NeuButton
          aria-label="close panel"
          kind="tertiary"
          color="blue"
          icon="close"
          size="small"
          onClick={() => handleModal(modalContentRef)}
        >
          Close
        </NeuButton>
      </CardHeader>
      {isUserRolesLoading || isGetDivisionsLoading ? (
        <ModalCardContent unit="content">
          <Loading label="user profile" />
        </ModalCardContent>
      ) : (
        <>
          <ModalCardContent unit="content">
            <UserInfo userDetails={selectedUser} />
            <InputHeading>User Role</InputHeading>
            <MultiSelectDropdown
              options={userRoleList}
              defaultselection={defaultUserRoles}
              selected={handleSelectedUserRoles}
              id="Modal-Provision-Select-User-Role"
              showArrow={false}
              searchable={false}
              chipsInside={false}
            />
            <NeuRadio
              data-testid="user-modal-access-level-radio-group"
              unit="group"
              label="Access Level"
              name="accessLevel"
              value={selectedAccessLevel || selectedUser?.access?.accessLevel}
              onNeuChange={e => onAccessLevelChange(e.detail.value)}
            >
              <NeuRadio
                data-testid="user-modal-access-level-radio-item-facility"
                label="Facility"
                name="accessLevel"
                unit="item"
                value="facility"
              />
              <NeuRadio
                data-testid="user-modal-access-level-radio-item-division"
                label="Division"
                name="accessLevel"
                unit="item"
                value="division"
              />
              <NeuRadio
                data-testid="user-modal-access-level-radio-item-corporate"
                label="Corporate"
                name="accessLevel"
                unit="item"
                value="master"
              />
            </NeuRadio>
            {showAccessLevel && accessLevelList && (
              <>
                <InputHeading>
                  {selectedAccessLevel.charAt(0).toUpperCase() +
                    selectedAccessLevel.slice(1)}
                </InputHeading>
                <MultiSelectDropdown
                  options={accessLevelList}
                  defaultselection={defaultSelection}
                  selected={handleSelected}
                  id="facility-division-select"
                  showArrow={false}
                  searchable={false}
                  chipsInside={false}
                />
              </>
            )}
          </ModalCardContent>
          <ModalFooter unit="footer">
            <NeuButton color="red" size="small" onClick={handleRevoke}>
              Revoke Access
            </NeuButton>
            <NeuButton
              color="blue"
              disabled={!showButton}
              size="small"
              onClick={handleSubmit}
            >
              Submit
            </NeuButton>
          </ModalFooter>
        </>
      )}
    </ProvisioningCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    facilities: state.ConfigReducer.userAccessibleFacilities,
    selectedUser: state.ModalReducer.modalData
  };
};

export default connect(mapReduxStateToProps)(AddProvisioningModal);
